// ============================================================================
// AXIOS CLIENT
// ============================================================================
// Two axios instances:
// - authApiClient: for discover, authenticate, refresh (NO token interceptor)
// - apiClient (axiosClient): for all authenticated business endpoints

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { apiConfig } from '../config/apiConfig';
import { setupAppInterceptors, setupAuthInterceptors } from './interceptors';

/**
 * Auth API Client — for discover, authenticate, refresh
 * NO token interceptor (these endpoints don't need Bearer tokens in headers)
 */
export const authApiClient: AxiosInstance = axios.create({
  baseURL: apiConfig.baseURL,
  timeout: apiConfig.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * App API Client — for all authenticated business endpoints
 * HAS token interceptor, tenant headers, proactive refresh
 */
export const axiosClient: AxiosInstance = axios.create({
  baseURL: apiConfig.baseURL,
  timeout: apiConfig.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Setup interceptors for both clients
setupAppInterceptors(axiosClient);
setupAuthInterceptors(authApiClient);

/**
 * HTTP Methods Helper Functions (use app client — authenticated)
 */
export const get = <T = any>(
  url: string,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  return axiosClient.get<T>(url, config);
};

export const post = <T = any, D = any>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  return axiosClient.post<T>(url, data, config);
};

export const put = <T = any, D = any>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  return axiosClient.put<T>(url, data, config);
};

export const patch = <T = any, D = any>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  return axiosClient.patch<T>(url, data, config);
};

export const del = <T = any>(
  url: string,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  return axiosClient.delete<T>(url, config);
};

export const uploadFile = <T = any>(
  url: string,
  file: File,
  onUploadProgress?: (progressEvent: any) => void
): Promise<AxiosResponse<T>> => {
  const formData = new FormData();
  formData.append('file', file);

  return axiosClient.post<T>(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress,
  });
};

export const downloadFile = (
  url: string,
  filename?: string
): Promise<void> => {
  return axiosClient
    .get(url, {
      responseType: 'blob',
    })
    .then(response => {
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute(
        'download',
        filename || `download-${Date.now()}`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    });
};

export default axiosClient;
